import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, api } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  ArrowLeft, ShieldCheck, FileDown, RefreshCw, Pencil, Mail,
  Circle, FileSearch, Send, CheckCircle2, XCircle, History,
  Settings2, Plus, X, Trash2, FolderDown, Download, ChevronDown, Upload, Paperclip,
  FileCheck, ExternalLink, Eye,
} from "lucide-react";
import { toast } from "sonner";

const STATUSES = [
  { k: "requested",              label: "Requested",              color: "bg-slate-100 text-slate-700 border-slate-200",       Icon: Circle },
  { k: "docs_collected",         label: "Docs collected",         color: "bg-blue-100 text-blue-800 border-blue-200",          Icon: FileSearch },
  { k: "submitted",              label: "Submitted",              color: "bg-amber-100 text-amber-900 border-amber-300",       Icon: Send },
  { k: "obtained",               label: "Obtained",               color: "bg-emerald-100 text-emerald-800 border-emerald-200", Icon: CheckCircle2 },
  { k: "moh_letter_submitted",   label: "MOH letter submitted",   color: "bg-indigo-100 text-indigo-800 border-indigo-200",    Icon: Send },
  { k: "moh_clearance_obtained", label: "MOH clearance obtained", color: "bg-teal-100 text-teal-800 border-teal-200",          Icon: CheckCircle2 },
  { k: "rejected",               label: "Rejected",               color: "bg-red-100 text-red-800 border-red-200",             Icon: XCircle },
  { k: "cancelled",              label: "Cancelled",              color: "bg-stone-100 text-stone-600 border-stone-200",       Icon: XCircle },
];
const STATUS_BY_KEY = Object.fromEntries(STATUSES.map((s) => [s.k, s]));

export default function MedicalLicenses() {
  const { user, hasPerm } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("__all__");
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);  // open dialog when not null
  const [saving, setSaving] = useState(false);
  const [licName, setLicName] = useState("");
  const [mohName, setMohName] = useState("");
  const [docsViewer, setDocsViewer] = useState(null); // row whose letters are being viewed
  const [showMoh, setShowMoh] = useState(false);      // MOH letters side panel
  const [includeCompleted, setIncludeCompleted] = useState(true);
  const [showDefaults, setShowDefaults] = useState(false);

  const canEdit = hasPerm("medical_licenses.update");
  const [printFrom, setPrintFrom] = useState("");
  const [printTo, setPrintTo] = useState("");

  const [selectedIds, setSelectedIds] = useState([]);

  const markObtained = async (r) => {
    if (!window.confirm(`Mark ${r.visitor_name}'s license as OBTAINED? (Documents are not required — e.g. obtained through an outside source.)`)) return;
    try {
      await api.put(`/bookings/${r.booking_id}/medical-license`, {
        status: "obtained", history_note: "Marked obtained (outside source — documents not required)",
      });
      toast.success(`${r.visitor_name} marked as obtained`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to update"); }
  };

  const bulkRemove = async () => {
    if (!window.confirm(`Remove ${selectedIds.length} entrie(s) from the medical license list? They will no longer be shown or tracked.`)) return;
    try {
      const { data } = await api.post("/medical-licenses/bulk-remove", { booking_ids: selectedIds });
      toast.success(`${data.removed} entrie(s) removed from the license list`);
      setSelectedIds([]);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to remove"); }
  };

  const cancelLicense = async (r) => {
    if (!window.confirm(`Cancel the medical license request for ${r.visitor_name}?`)) return;
    try {
      await api.put(`/bookings/${r.booking_id}/medical-license`, {
        status: "cancelled", history_note: "License request cancelled",
      });
      toast.success("License request cancelled");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to cancel"); }
  };

  const printList = () => {
    const inRange = rows.filter((r) => {
      if (["obtained", "rejected", "cancelled"].includes(r.status)) return false;
      if (printFrom && (r.stay_from || "") < printFrom) return false;
      if (printTo && (r.stay_from || "") > printTo) return false;
      return true;
    });
    const w = window.open("", "_blank");
    w.document.write(`<html><head><title>Medical licenses needed</title>
      <style>body{font-family:sans-serif;padding:24px}h1{font-size:18px}table{border-collapse:collapse;width:100%}
      th,td{border:1px solid #ccc;padding:6px 8px;font-size:12px;text-align:left}th{background:#f2f2ef}</style></head><body>
      <h1>Medical licenses needed ${printFrom || printTo ? `(arrivals ${printFrom || "…"} → ${printTo || "…"})` : ""}</h1>
      <p>${inRange.length} license(s) · printed ${new Date().toLocaleString()}</p>
      <table><tr><th>Visitor ID</th><th>Visitor</th><th>Profession</th><th>Arrival</th><th>Departure</th><th>Status</th><th>License #</th><th>Missing docs</th></tr>
      ${inRange.map((r) => `<tr><td>${r.visitor_no || ""}</td><td>${r.visitor_name}</td><td>${r.profession || ""}</td><td>${r.stay_from}</td>
        <td>${r.stay_to}</td><td>${r.status}</td><td>${r.license_number || ""}</td>
        <td>${(r.docs_missing || []).join(", ")}</td></tr>`).join("")}
      </table></body></html>`);
    w.document.close();
    w.focus();
    w.print();
  };

  const uploadLicenseDoc = async (file, requirement = "Medical license certificate", customName = "") => {
    if (!file || !editing) return;
    const fd = new FormData();
    fd.append("entity_type", "booking");
    fd.append("entity_id", editing.booking_id);
    fd.append("requirement", requirement);
    let name = file.name;
    const trimmed = (customName || "").trim();
    if (trimmed) {
      const ext = file.name.includes(".") ? file.name.slice(file.name.lastIndexOf(".")) : "";
      name = trimmed.toLowerCase().endsWith(ext.toLowerCase()) ? trimmed : trimmed + ext;
    }
    fd.append("file", file, name);
    try {
      await api.post("/documents/upload", fd);
      toast.success(`${name} uploaded to this license record`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/medical-licenses?include_completed=${includeCompleted}`);
      setRows(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load");
    } finally { setLoading(false); }
  }, [includeCompleted]);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    return rows.filter((r) => {
      if (filterStatus !== "__all__" && r.status !== filterStatus) return false;
      const s = search.trim().toLowerCase();
      if (s && ![r.visitor_name, r.profession, r.license_number, r.notes].some((v) => (v || "").toLowerCase().includes(s))) return false;
      return true;
    });
  }, [rows, filterStatus, search]);

  const counts = useMemo(() => {
    const c = { total: rows.length, requested: 0, docs_collected: 0, submitted: 0, obtained: 0, rejected: 0 };
    rows.forEach((r) => { c[r.status] = (c[r.status] || 0) + 1; });
    return c;
  }, [rows]);

  const downloadExport = async (format) => {
    try {
      const res = await api.get(`/medical-licenses/export?format=${format}`, { responseType: "blob" });
      const ext = format === "xlsx" ? "xlsx" : "csv";
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `medical-licenses-${new Date().toISOString().slice(0,10)}.${ext}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.response?.data?.detail || "Export failed"); }
  };

  const openEdit = (r) => setEditing({
    booking_id: r.booking_id,
    request_id: r.request_id,
    status: r.status,
    notes: r.notes || "",
    license_number: r.license_number || "",
    fee_amount: r.fee_amount || 0,
    fee_currency: r.fee_currency || "KES",
    profession: r.profession || "",
    recipients: [...(r.recipients || [])],
    history_note: "",
    visitor_name: r.visitor_name,
    visitor_no: r.visitor_no || "",
    history: r.history || [],
    docs_feature_enabled: !!r.docs_feature_enabled,
    docs_required: r.docs_required || [],
    docs_satisfied: r.docs_satisfied || {},
    docs_missing: r.docs_missing || [],
    documents: r.documents || [],
    showFiles: false,
  });

  const downloadAllDocs = async () => {
    try {
      const res = await api.get(`/documents/booking/${editing.booking_id}/zip`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${editing.visitor_name}${editing.visitor_no ? " " + editing.visitor_no : ""}.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("All documents downloaded");
    } catch (e) {
      toast.error(e.response?.status === 404 ? "No documents to download yet" : (e.response?.data?.detail || "Download failed"));
    }
  };

  const downloadDoc = async (d) => {
    try {
      const res = await api.get(`/documents/${d.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = d.filename;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.response?.data?.detail || "Download failed"); }
  };

  const recomputeDocs = (docs, required) => {
    const satisfied = {};
    docs.forEach((d) => {
      if (d.requirement && required.includes(d.requirement) && !satisfied[d.requirement]) satisfied[d.requirement] = d.filename;
    });
    return { satisfied, missing: required.filter((r) => !satisfied[r]) };
  };

  const tagDoc = async (docId, requirement) => {
    const val = requirement === "__none__" ? null : requirement;
    try {
      await api.put(`/documents/${docId}/requirement`, { requirement: val });
      const docs = editing.documents.map((d) => (d.id === docId ? { ...d, requirement: val } : d));
      const { satisfied, missing } = recomputeDocs(docs, editing.docs_required);
      setEditing({ ...editing, documents: docs, docs_satisfied: satisfied, docs_missing: missing });
      toast.success(val ? `Tagged as ${val}` : "Tag removed");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Tagging failed"); }
  };

  const requestDocs = async () => {
    try {
      const { data } = await api.post(`/bookings/${editing.booking_id}/medical-license/request-docs`);
      toast.success(`Missing-documents request sent to ${(data.recipients || []).join(", ")}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to send request"); }
  };

  const save = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      await api.put(`/bookings/${editing.booking_id}/medical-license`, {
        status: editing.status,
        notes: editing.notes,
        license_number: editing.license_number,
        fee_amount: editing.fee_amount,
        fee_currency: editing.fee_currency,
        profession: editing.profession,
        recipients: editing.recipients,
        history_note: editing.history_note,
      });
      toast.success("License workflow updated");
      setEditing(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6 fade-in" data-testid="medical-licenses-page">
      <Button variant="ghost" onClick={() => navigate(-1)} className="text-[#525C58]" data-testid="back-btn">
        <ArrowLeft className="h-4 w-4 mr-2" /> Back
      </Button>

      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-4xl font-semibold tracking-tight flex items-center gap-3" style={{ fontFamily: "Manrope, sans-serif" }}>
            <ShieldCheck className="h-8 w-8 text-[#40916C]" /> Medical licenses
          </h1>
          <p className="text-[#525C58] mt-1">Track every clinician&apos;s licensing status — from initial request through to certificate obtained.</p>
          {!canEdit && (
            <p className="text-[11px] text-[#7A8A84] mt-1">View-only — only Booking and Hospitality coordinators can advance the workflow.</p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {canEdit && selectedIds.length > 0 && (
            <Button variant="outline" size="sm" onClick={bulkRemove}
                    className="border-red-300 text-red-700 hover:bg-red-50" data-testid="ml-bulk-remove-btn">
              <Trash2 className="h-3.5 w-3.5 mr-1" /> Remove {selectedIds.length} from list
            </Button>
          )}
          <div className="flex items-end gap-1">
            <Input type="date" className="h-8 w-34 text-xs" value={printFrom} onChange={(e) => setPrintFrom(e.target.value)} data-testid="print-from" />
            <Input type="date" className="h-8 w-34 text-xs" value={printTo} onChange={(e) => setPrintTo(e.target.value)} data-testid="print-to" />
            <Button variant="outline" size="sm" onClick={printList} className="border-[#1B4332]/20" data-testid="print-list-btn">
              <FileDown className="h-3.5 w-3.5 mr-1" /> Print list
            </Button>
          </div>
          <Button variant="outline" size="sm" onClick={() => setShowMoh((v) => !v)}
                  className={showMoh ? "bg-indigo-600 text-white border-indigo-600 hover:bg-indigo-700 hover:text-white" : "border-indigo-300 text-indigo-800 hover:bg-indigo-50"}
                  data-testid="moh-letters-btn">
            <FileCheck className="h-3.5 w-3.5 mr-1" /> MOH letters
          </Button>
          {canEdit && (
            <Button variant="outline" size="sm" onClick={() => setShowDefaults(true)} className="border-[#1B4332]/20" data-testid="ml-defaults-btn">
              <Settings2 className="h-3.5 w-3.5 mr-1" /> Defaults
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={() => downloadExport("csv")} className="border-[#1B4332]/20" data-testid="export-csv-btn">
            <FileDown className="h-3.5 w-3.5 mr-1" /> CSV
          </Button>
          <Button variant="outline" size="sm" onClick={() => downloadExport("xlsx")} className="border-[#1B4332]/20" data-testid="export-xlsx-btn">
            <FileDown className="h-3.5 w-3.5 mr-1" /> Excel
          </Button>
          <Button variant="outline" size="sm" onClick={load} data-testid="refresh-btn">
            <RefreshCw className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      {/* Status counts */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="status-counts">
        <Card className="border-[#E8E8E6]"><CardContent className="py-3">
          <p className="text-[10px] uppercase tracking-wider text-[#7A8A84] font-semibold">Total</p>
          <p className="text-2xl font-bold tabular-nums text-[#1B4332]">{counts.total}</p>
        </CardContent></Card>
        {STATUSES.map(({ k, label, color, Icon }) => (
          <Card key={k} className="border-[#E8E8E6] cursor-pointer hover:border-[#1B4332]/30"
                onClick={() => setFilterStatus(filterStatus === k ? "__all__" : k)}
                data-testid={`count-${k}`}>
            <CardContent className="py-3">
              <p className="text-[10px] uppercase tracking-wider text-[#7A8A84] font-semibold flex items-center gap-1"><Icon className="h-3 w-3" /> {label}</p>
              <p className="text-2xl font-bold tabular-nums">{counts[k] || 0}</p>
              {filterStatus === k && <Badge className={`${color} border text-[9px] mt-1`}>filtered</Badge>}
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="flex flex-col xl:flex-row gap-4 items-start">
      <Card className="border-[#E8E8E6] flex-1 min-w-0 w-full">
        <CardHeader>
          <CardTitle className="text-base flex items-center justify-between gap-3 flex-wrap">
            <span>{filtered.length} request{filtered.length === 1 ? "" : "s"}</span>
            <div className="flex items-center gap-2 flex-wrap">
              <Input placeholder="Search visitor / profession / license #" value={search} onChange={(e) => setSearch(e.target.value)} className="w-64 text-sm" data-testid="search-input" />
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-44 text-sm" data-testid="filter-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All statuses</SelectItem>
                  {STATUSES.map((s) => <SelectItem key={s.k} value={s.k}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <label className="flex items-center gap-1 text-xs text-[#525C58]">
                <input type="checkbox" checked={includeCompleted} onChange={(e) => setIncludeCompleted(e.target.checked)} data-testid="include-completed-toggle" />
                Show completed
              </label>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-[#7A8A84] text-center py-8">Loading…</p>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12 text-[#7A8A84]">
              <ShieldCheck className="h-10 w-10 mx-auto text-[#B8C2BC] mb-2" />
              <p>No license requests match these filters.</p>
              <p className="text-[11px] mt-1">Tick &quot;Visitor needs a medical license&quot; on a booking to start tracking.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table data-testid="licenses-table">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-8">
                      <input type="checkbox" data-testid="ml-select-all"
                             checked={filtered.length > 0 && selectedIds.length === filtered.length}
                             onChange={(e) => setSelectedIds(e.target.checked ? filtered.map((r) => r.booking_id) : [])} />
                    </TableHead>
                    <TableHead>Visitor</TableHead>
                    <TableHead>Profession</TableHead>
                    <TableHead>Stay</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Docs</TableHead>
                    <TableHead>License #</TableHead>
                    <TableHead className="text-right">Fee</TableHead>
                    <TableHead>Recipients</TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((r) => {
                    const meta = STATUS_BY_KEY[r.status] || STATUS_BY_KEY.requested;
                    const Icon = meta.Icon;
                    return (
                      <TableRow key={r.booking_id} data-testid={`license-row-${r.booking_id}`}>
                        <TableCell>
                          <input type="checkbox" checked={selectedIds.includes(r.booking_id)}
                                 onChange={(e) => setSelectedIds((p) => e.target.checked ? [...p, r.booking_id] : p.filter((x) => x !== r.booking_id))}
                                 data-testid={`ml-select-${r.booking_id}`} />
                        </TableCell>
                        <TableCell className="font-medium">{r.visitor_no ? `${r.visitor_no} · ` : ""}{r.visitor_name}</TableCell>
                        <TableCell className="text-sm text-[#525C58]">{r.profession || "—"}</TableCell>
                        <TableCell className="text-sm text-[#525C58]">{r.stay_from} → {r.stay_to}</TableCell>
                        <TableCell>
                          <Badge className={`${meta.color} border text-[10px] inline-flex items-center gap-1`}>
                            <Icon className="h-3 w-3" /> {meta.label}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1.5">
                            {r.docs_feature_enabled && (r.docs_required || []).length > 0 ? (
                              <Badge
                                className={`border text-[10px] tabular-nums ${
                                  (r.docs_missing || []).length === 0
                                    ? "bg-emerald-100 text-emerald-800 border-emerald-200"
                                    : "bg-amber-100 text-amber-900 border-amber-300"
                                }`}
                                title={(r.docs_missing || []).length ? `Missing: ${r.docs_missing.join(", ")}` : "All required documents in"}
                                data-testid={`docs-badge-${r.booking_id}`}
                              >
                                {(r.docs_required.length - (r.docs_missing || []).length)}/{r.docs_required.length} docs
                              </Badge>
                            ) : <span className="text-[#A3ACA8] text-xs">—</span>}
                            {(r.documents || []).length > 0 && (
                              <button type="button" onClick={() => setDocsViewer(r)}
                                      className="inline-flex items-center gap-0.5 text-[11px] text-[#40916C] hover:text-[#1B4332] hover:underline"
                                      title="View uploaded letters & documents"
                                      data-testid={`view-docs-${r.booking_id}`}>
                                <Paperclip className="h-3 w-3" /> {(r.documents || []).length}
                              </button>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-sm tabular-nums">{r.license_number || <span className="text-[#A3ACA8]">—</span>}</TableCell>
                        <TableCell className="text-right tabular-nums text-sm">
                          {r.fee_amount > 0 ? `${r.fee_currency} ${Number(r.fee_amount).toLocaleString()}` : <span className="text-[#A3ACA8]">—</span>}
                        </TableCell>
                        <TableCell className="text-xs text-[#525C58]">
                          {(r.recipients || []).length === 0 ? <span className="text-amber-700">none</span> : (
                            <span title={r.recipients.join(", ")}>
                              <Mail className="h-3 w-3 inline mr-1" />
                              {r.recipients.length}
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          {canEdit && (
                            <>
                              <Button size="sm" variant="ghost" onClick={() => openEdit(r)} className="text-[#1B4332]" data-testid={`edit-license-${r.booking_id}`}>
                                <Pencil className="h-3.5 w-3.5" />
                              </Button>
                              {!["obtained", "moh_letter_submitted", "moh_clearance_obtained", "rejected", "cancelled"].includes(r.status) && (
                                <Button size="sm" variant="ghost" onClick={() => markObtained(r)} className="text-emerald-700"
                                        title="Mark obtained — documents not required (outside source)"
                                        data-testid={`mark-obtained-${r.booking_id}`}>
                                  <CheckCircle2 className="h-3.5 w-3.5" />
                                </Button>
                              )}
                              {!["obtained", "rejected", "cancelled"].includes(r.status) && (
                                <Button size="sm" variant="ghost" onClick={() => cancelLicense(r)} className="text-red-600"
                                        title="Cancel this license request" data-testid={`cancel-license-${r.booking_id}`}>
                                  <XCircle className="h-3.5 w-3.5" />
                                </Button>
                              )}
                            </>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {showMoh && <MohLettersPanel canEdit={canEdit} onClose={() => setShowMoh(false)} />}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-xl" data-testid="license-edit-dialog">
          <DialogHeader>
            <DialogTitle>Update license workflow</DialogTitle>
            <DialogDescription>{editing?.visitor_name}</DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label>Status</Label>
                  <Select value={editing.status} onValueChange={(v) => setEditing({ ...editing, status: v })}>
                    <SelectTrigger data-testid="dialog-status"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {STATUSES.map((s) => <SelectItem key={s.k} value={s.k}>{s.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label>License # (when obtained)</Label>
                  <Input value={editing.license_number} onChange={(e) => setEditing({ ...editing, license_number: e.target.value })}
                         placeholder="e.g. KMPDC/2026/8842" data-testid="dialog-license-number" />
                </div>
              </div>

              <div className="space-y-1">
                <Label>Profession</Label>
                <Input value={editing.profession} onChange={(e) => setEditing({ ...editing, profession: e.target.value })} data-testid="dialog-profession" />
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1 col-span-2">
                  <Label>Fee (added to invoice)</Label>
                  <Input type="number" min={0} value={editing.fee_amount}
                         onChange={(e) => setEditing({ ...editing, fee_amount: Number(e.target.value) })} data-testid="dialog-fee" />
                </div>
                <div className="space-y-1">
                  <Label>Currency</Label>
                  <Select value={editing.fee_currency} onValueChange={(v) => setEditing({ ...editing, fee_currency: v })}>
                    <SelectTrigger data-testid="dialog-fee-currency"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="KES">KES</SelectItem>
                      <SelectItem value="USD">USD</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-1">
                <Label>Notes</Label>
                <Textarea rows={2} value={editing.notes} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} data-testid="dialog-notes" />
              </div>

              {editing.docs_feature_enabled && (editing.docs_required || []).length > 0 && (
                <div className="rounded-lg border border-[#E8E8E6] p-3 space-y-2" data-testid="docs-checklist">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <p className="text-[10px] uppercase tracking-wider text-[#7A8A84] font-semibold flex items-center gap-1">
                      <FileSearch className="h-3 w-3" /> Documents
                      <span className="text-[#525C58] normal-case tracking-normal">
                        ({editing.docs_required.length - (editing.docs_missing || []).length}/{editing.docs_required.length} checklist · {(editing.documents || []).length} files)
                      </span>
                    </p>
                    <div className="flex items-center gap-1.5">
                      <Button type="button" size="sm" variant="outline"
                              onClick={downloadAllDocs}
                              disabled={(editing.documents || []).length === 0}
                              className="border-[#1B4332]/20 text-[#1B4332] hover:bg-[#1B4332] hover:text-white h-7 text-xs"
                              data-testid="download-all-docs-btn">
                        <FolderDown className="h-3 w-3 mr-1" /> Download all
                      </Button>
                      <Button type="button" size="sm" variant="outline"
                              onClick={requestDocs}
                              disabled={(editing.docs_missing || []).length === 0}
                              className="border-amber-300 text-amber-900 hover:bg-amber-50 h-7 text-xs"
                              data-testid="request-docs-btn">
                        <Send className="h-3 w-3 mr-1" /> Request missing
                      </Button>
                    </div>
                  </div>

                  {/* Compact checklist — requirement names only */}
                  <div className="flex flex-wrap gap-1.5">
                    {editing.docs_required.map((req) => (
                      <span key={req} data-testid={`checklist-item-${req}`}
                            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] border ${
                              editing.docs_satisfied[req]
                                ? "bg-emerald-50 text-emerald-800 border-emerald-200"
                                : "bg-amber-50 text-amber-800 border-amber-300"
                            }`}
                            title={editing.docs_satisfied[req] ? `Satisfied by ${editing.docs_satisfied[req]}` : "Missing"}>
                        {editing.docs_satisfied[req]
                          ? <CheckCircle2 className="h-3 w-3" />
                          : <XCircle className="h-3 w-3" />}
                        {req}
                      </span>
                    ))}
                  </div>

                  {/* Files — collapsed by default to keep the dialog clean */}
                  {(editing.documents || []).length > 0 ? (
                    <div className="pt-1 border-t border-[#E8E8E6]">
                      <button type="button"
                              onClick={() => setEditing({ ...editing, showFiles: !editing.showFiles })}
                              className="w-full flex items-center justify-between text-[11px] text-[#525C58] hover:text-[#1B4332] py-1"
                              data-testid="toggle-files-btn">
                        <span>{editing.showFiles ? "Hide" : "Show"} {(editing.documents || []).length} uploaded files</span>
                        <ChevronDown className={`h-3.5 w-3.5 transition-transform ${editing.showFiles ? "rotate-180" : ""}`} />
                      </button>
                      {editing.showFiles && (
                        <div className="space-y-1 mt-1 max-h-56 overflow-y-auto pr-1">
                          {editing.documents.map((d) => (
                            <div key={d.id} className="flex items-center gap-2 text-xs" data-testid={`doc-tag-row-${d.id}`}>
                              <button type="button" onClick={() => downloadDoc(d)}
                                      className="shrink-0 text-[#40916C] hover:text-[#1B4332]"
                                      title={`Download ${d.filename}`} data-testid={`doc-download-${d.id}`}>
                                <Download className="h-3.5 w-3.5" />
                              </button>
                              <span className="truncate flex-1" title={d.filename}>{d.filename}</span>
                              <Select value={d.requirement || "__none__"} onValueChange={(v) => tagDoc(d.id, v)}>
                                <SelectTrigger className="h-7 w-40 text-xs shrink-0" data-testid={`doc-tag-select-${d.id}`}>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="__none__">Untagged</SelectItem>
                                  {editing.docs_required.map((req) => <SelectItem key={req} value={req}>{req}</SelectItem>)}
                                </SelectContent>
                              </Select>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="text-[11px] text-[#7A8A84] italic">No files uploaded yet — documents attached to the booking or its original request appear here.</p>
                  )}
                </div>
              )}

              <div className="space-y-1">
                <Label>Comment for the audit history</Label>
                <Input value={editing.history_note} onChange={(e) => setEditing({ ...editing, history_note: e.target.value })}
                       placeholder="Optional — what changed and why" data-testid="dialog-history-note" />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-1.5 rounded-lg border border-emerald-200 bg-emerald-50/40 p-3">
                  <Label className="text-xs font-semibold text-emerald-900">Upload medical license (certificate / permit)</Label>
                  <Input value={licName} onChange={(e) => setLicName(e.target.value)}
                         placeholder="Document name (optional)" className="h-8 text-xs bg-white" data-testid="license-name-input" />
                  <Input type="file" accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.webp" className="h-9 text-xs bg-white"
                         onChange={(e) => { uploadLicenseDoc(e.target.files[0], "Medical license certificate", licName); e.target.value = ""; setLicName(""); }}
                         data-testid="license-upload-input" />
                </div>
                <div className="space-y-1.5 rounded-lg border border-indigo-200 bg-indigo-50/40 p-3">
                  <Label className="text-xs font-semibold text-indigo-900">Upload MOH Clearance letter</Label>
                  <Input value={mohName} onChange={(e) => setMohName(e.target.value)}
                         placeholder="Letter name (optional) — e.g. MOH Clearance - Dr Fite" className="h-8 text-xs bg-white" data-testid="moh-name-input" />
                  <Input type="file" accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.webp" className="h-9 text-xs bg-white"
                         onChange={(e) => { uploadLicenseDoc(e.target.files[0], "MOH Clearance letter", mohName); e.target.value = ""; setMohName(""); }}
                         data-testid="moh-upload-input" />
                </div>
              </div>

              {editing.history.length > 0 && (
                <div className="rounded-lg border border-[#E8E8E6] p-3 max-h-40 overflow-y-auto" data-testid="history-list">
                  <p className="text-[10px] uppercase tracking-wider text-[#7A8A84] font-semibold mb-2 flex items-center gap-1">
                    <History className="h-3 w-3" /> Workflow history
                  </p>
                  <ul className="text-[11px] space-y-1.5">
                    {editing.history.slice().reverse().map((h, i) => (
                      <li key={i} className="border-l-2 border-[#1B4332]/20 pl-2">
                        <div className="text-[#1C1F1E] font-medium">{(STATUS_BY_KEY[h.status]?.label) || h.status}</div>
                        <div className="text-[#7A8A84]">{h.note}</div>
                        <div className="text-[#A3ACA8]">{(h.at || "").slice(0, 19).replace("T", " ")} · {h.by}</div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-[#1B4332] hover:bg-[#2D6A4F]" data-testid="dialog-save-btn">
              {saving ? "Saving…" : "Save changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {showDefaults && <MlDefaultsDialog onClose={() => setShowDefaults(false)} />}

      {docsViewer && (
        <DocsViewerDialog row={docsViewer} canEdit={canEdit}
                          onChanged={() => { load(); }}
                          onClose={() => setDocsViewer(null)} />
      )}
    </div>
  );
}

function DocsViewerDialog({ row, canEdit, onChanged, onClose }) {
  const [docs, setDocs] = useState(row.documents || []);
  const [mohName, setMohName] = useState("");
  const [uploading, setUploading] = useState(false);

  const download = async (d) => {
    try {
      const res = await api.get(`/documents/${d.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = d.filename;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.response?.data?.detail || "Download failed"); }
  };

  const downloadAll = async () => {
    try {
      const res = await api.get(`/documents/booking/${row.booking_id}/zip`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${row.visitor_name}${row.visitor_no ? " " + row.visitor_no : ""}.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.response?.data?.detail || "Download failed"); }
  };

  const uploadMoh = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("entity_type", "booking");
      fd.append("entity_id", row.booking_id);
      fd.append("requirement", "MOH Clearance letter");
      let name = file.name;
      const trimmed = mohName.trim();
      if (trimmed) {
        const ext = file.name.includes(".") ? file.name.slice(file.name.lastIndexOf(".")) : "";
        name = trimmed.toLowerCase().endsWith(ext.toLowerCase()) ? trimmed : trimmed + ext;
      }
      fd.append("file", file, name);
      const { data } = await api.post("/documents/upload", fd);
      setDocs((prev) => [data, ...prev]);
      setMohName("");
      toast.success(`${name} uploaded`);
      onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="docs-viewer-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Paperclip className="h-5 w-5 text-[#1B4332]" /> Letters &amp; documents — {row.visitor_name}
          </DialogTitle>
          <DialogDescription>{row.visitor_no ? `${row.visitor_no} · ` : ""}All files on this license record.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {docs.length === 0 ? (
            <p className="text-sm text-[#7A8A84] italic text-center py-4">No documents uploaded yet.</p>
          ) : (
            <div className="space-y-1 max-h-72 overflow-y-auto pr-1">
              {docs.map((d) => (
                <div key={d.id} className="flex items-center gap-2 text-sm p-2 rounded-lg hover:bg-[#F4F4F1]" data-testid={`viewer-doc-${d.id}`}>
                  <button type="button" onClick={() => download(d)}
                          className="shrink-0 text-[#40916C] hover:text-[#1B4332]" title={`Download ${d.filename}`}
                          data-testid={`viewer-download-${d.id}`}>
                    <Download className="h-4 w-4" />
                  </button>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[#1C1F1E]" title={d.filename}>{d.filename}</p>
                    <p className="text-[10px] text-[#7A8A84]">
                      {d.requirement || "Untagged"}{d.uploaded_by_name ? ` · ${d.uploaded_by_name}` : ""}
                    </p>
                  </div>
                  {d.requirement === "MOH Clearance letter" && (
                    <Badge className="bg-indigo-100 text-indigo-800 border-indigo-200 border text-[9px] shrink-0">MOH</Badge>
                  )}
                </div>
              ))}
            </div>
          )}
          {canEdit && (
            <div className="space-y-1.5 rounded-lg border border-indigo-200 bg-indigo-50/40 p-3">
              <Label className="text-xs font-semibold text-indigo-900">Upload MOH Clearance letter</Label>
              <Input value={mohName} onChange={(e) => setMohName(e.target.value)}
                     placeholder="Letter name (optional)" className="h-8 text-xs bg-white" data-testid="viewer-moh-name" />
              <Input type="file" accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.webp" className="h-9 text-xs bg-white" disabled={uploading}
                     onChange={(e) => { uploadMoh(e.target.files[0]); e.target.value = ""; }}
                     data-testid="viewer-moh-upload" />
            </div>
          )}
        </div>
        <DialogFooter className="flex items-center justify-between sm:justify-between">
          <Button variant="outline" size="sm" onClick={downloadAll} disabled={docs.length === 0}
                  className="border-[#1B4332]/20 text-[#1B4332]" data-testid="viewer-download-all">
            <FolderDown className="h-3.5 w-3.5 mr-1" /> Download all (ZIP)
          </Button>
          <Button variant="outline" size="sm" onClick={onClose} data-testid="viewer-close">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MlDefaultsDialog({ onClose }) {
  const [form, setForm] = useState({ default_fee_amount: 0, default_fee_currency: "KES", default_recipients: [], docs_feature_enabled: true, required_docs: [], auto_chase_enabled: true, auto_chase_days: 3 });
  const [draft, setDraft] = useState("");
  const [reqDraft, setReqDraft] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/housing/medical-license-settings")
      .then(({ data }) => setForm({
        default_fee_amount: data.default_fee_amount || 0,
        default_fee_currency: data.default_fee_currency || "KES",
        default_recipients: data.default_recipients || [],
        docs_feature_enabled: data.docs_feature_enabled !== false,
        required_docs: data.required_docs || [],
        auto_chase_enabled: data.auto_chase_enabled !== false,
        auto_chase_days: data.auto_chase_days || 3,
      }))
      .catch(() => toast.error("Failed to load defaults"))
      .finally(() => setLoading(false));
  }, []);

  const addRecipient = () => {
    const v = (draft || "").trim();
    if (!v) return;
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v)) { toast.error("Enter a valid email"); return; }
    if (form.default_recipients.includes(v)) { setDraft(""); return; }
    setForm({ ...form, default_recipients: [...form.default_recipients, v] });
    setDraft("");
  };
  const removeRecipient = (em) => setForm({
    ...form,
    default_recipients: form.default_recipients.filter((r) => r !== em),
  });

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/housing/medical-license-settings", form);
      toast.success("Defaults saved — new bookings will pre-fill these recipients");
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="ml-defaults-dialog">
        <DialogHeader>
          <DialogTitle>Medical license defaults</DialogTitle>
          <DialogDescription>
            The default recipients pre-fill every new booking that needs a medical license,
            so you don&apos;t re-enter them each time. Bookings without their own list also
            fall back to these recipients when the notification email is sent.
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <p className="text-[#7A8A84] text-center py-6">Loading…</p>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Default fee</Label>
                <Input type="number" min="0" value={form.default_fee_amount}
                       onChange={(e) => setForm({ ...form, default_fee_amount: Number(e.target.value) })}
                       data-testid="ml-default-fee-input" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Currency</Label>
                <Select value={form.default_fee_currency}
                        onValueChange={(v) => setForm({ ...form, default_fee_currency: v })}>
                  <SelectTrigger data-testid="ml-default-currency"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="KES">KES</SelectItem>
                    <SelectItem value="USD">USD</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-1">
              <Label className="text-xs flex items-center gap-1"><Mail className="h-3 w-3" /> Default recipients</Label>
              <div className="flex flex-wrap gap-1.5" data-testid="ml-default-recipient-chips">
                {form.default_recipients.map((em) => (
                  <span key={em} className="inline-flex items-center gap-1 bg-[#F4F4F1] border border-[#E8E8E6] rounded-full px-2 py-0.5 text-xs">
                    {em}
                    <button type="button" onClick={() => removeRecipient(em)} className="text-red-600 hover:text-red-700" data-testid={`ml-remove-default-${em}`}>
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
                {form.default_recipients.length === 0 && (
                  <span className="text-[11px] text-[#7A8A84]">No defaults yet — add the licensing team&apos;s emails.</span>
                )}
              </div>
              <div className="flex gap-2">
                <Input value={draft} onChange={(e) => setDraft(e.target.value)}
                       onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addRecipient(); } }}
                       placeholder="email@example.com" className="flex-1" data-testid="ml-default-recipient-input" />
                <Button type="button" size="sm" variant="outline" onClick={addRecipient} className="border-[#1B4332]/20" data-testid="ml-add-default-btn">
                  <Plus className="h-3.5 w-3.5 mr-1" /> Add
                </Button>
              </div>
            </div>

            <div className="rounded-lg border border-[#E8E8E6] p-3 space-y-2" data-testid="ml-docs-settings">
              <label className="flex items-start gap-2 cursor-pointer">
                <input type="checkbox" checked={form.docs_feature_enabled}
                       onChange={(e) => setForm({ ...form, docs_feature_enabled: e.target.checked })}
                       className="mt-0.5" data-testid="ml-docs-feature-toggle" />
                <div>
                  <p className="text-sm font-medium text-[#1C1F1E]">Document checklist &amp; alerts</p>
                  <p className="text-[11px] text-[#7A8A84]">
                    Track required license documents per visitor, auto-email recipients on every new upload,
                    chase agencies for missing files, and send a &quot;ready to process&quot; email with the full
                    package once complete.
                  </p>
                </div>
              </label>
              {form.docs_feature_enabled && (
                <div className="space-y-1 pl-6">
                  <Label className="text-xs">Required documents</Label>
                  <div className="flex flex-wrap gap-1.5" data-testid="ml-required-doc-chips">
                    {form.required_docs.map((d) => (
                      <span key={d} className="inline-flex items-center gap-1 bg-[#F4F4F1] border border-[#E8E8E6] rounded-full px-2 py-0.5 text-xs">
                        {d}
                        <button type="button" onClick={() => setForm({ ...form, required_docs: form.required_docs.filter((x) => x !== d) })}
                                className="text-red-600 hover:text-red-700" data-testid={`ml-remove-doc-${d}`}>
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                    {form.required_docs.length === 0 && (
                      <span className="text-[11px] text-amber-700">No documents required — the checklist is effectively off.</span>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Input value={reqDraft} onChange={(e) => setReqDraft(e.target.value)}
                           onKeyDown={(e) => {
                             if (e.key === "Enter") {
                               e.preventDefault();
                               const v = reqDraft.trim();
                               if (v && !form.required_docs.includes(v)) setForm({ ...form, required_docs: [...form.required_docs, v] });
                               setReqDraft("");
                             }
                           }}
                           placeholder="e.g. Letter of good standing" className="flex-1" data-testid="ml-required-doc-input" />
                    <Button type="button" size="sm" variant="outline" className="border-[#1B4332]/20"
                            onClick={() => {
                              const v = reqDraft.trim();
                              if (v && !form.required_docs.includes(v)) setForm({ ...form, required_docs: [...form.required_docs, v] });
                              setReqDraft("");
                            }}
                            data-testid="ml-add-doc-btn">
                      <Plus className="h-3.5 w-3.5 mr-1" /> Add
                    </Button>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-[#E8E8E6]" data-testid="ml-auto-chase-row">
                    <label className="flex items-center gap-2 text-xs cursor-pointer">
                      <input type="checkbox" checked={form.auto_chase_enabled}
                             onChange={(e) => setForm({ ...form, auto_chase_enabled: e.target.checked })}
                             data-testid="ml-auto-chase-toggle" />
                      Auto-resend the missing-docs email every
                    </label>
                    <Input type="number" min={1} max={30} value={form.auto_chase_days}
                           onChange={(e) => setForm({ ...form, auto_chase_days: Number(e.target.value) })}
                           className="w-16 h-7 text-xs" disabled={!form.auto_chase_enabled}
                           data-testid="ml-auto-chase-days" />
                    <span className="text-xs text-[#525C58]">day(s) until the package is complete</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving || loading} className="bg-[#1B4332] hover:bg-[#2D6A4F]" data-testid="ml-defaults-save-btn">
            {saving ? "Saving…" : "Save defaults"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MohLettersPanel({ canEdit, onClose }) {
  const [letters, setLetters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null); // {id, url, filename}

  const loadLetters = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/moh-letters");
      setLetters(data || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load MOH letters"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { loadLetters(); }, [loadLetters]);
  useEffect(() => () => { if (preview?.url) URL.revokeObjectURL(preview.url); }, [preview]);

  const upload = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) { toast.error("MOH letters must be PDF files"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (name.trim()) fd.append("name", name.trim());
      const { data } = await api.post("/moh-letters/upload", fd);
      setLetters((prev) => [data, ...prev]);
      setName("");
      toast.success(`${data.filename} added`);
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); }
  };

  const view = async (l) => {
    try {
      const res = await api.get(`/moh-letters/${l.id}/file`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      if (preview?.url) URL.revokeObjectURL(preview.url);
      setPreview({ id: l.id, url, filename: l.filename });
    } catch (e) { toast.error(e.response?.data?.detail || "Could not open letter"); }
  };

  const openTab = () => { if (preview?.url) window.open(preview.url, "_blank"); };

  const download = async (l) => {
    try {
      const res = await api.get(`/moh-letters/${l.id}/file?download=true`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = l.filename;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.response?.data?.detail || "Download failed"); }
  };

  const remove = async (l) => {
    if (!window.confirm(`Delete "${l.filename}"?`)) return;
    try {
      await api.delete(`/moh-letters/${l.id}`);
      setLetters((prev) => prev.filter((x) => x.id !== l.id));
      if (preview?.id === l.id) setPreview(null);
      toast.success("Letter deleted");
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  return (
    <Card className="border-indigo-200 w-full xl:w-[26rem] shrink-0 xl:sticky xl:top-4" data-testid="moh-letters-panel">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center justify-between gap-2">
          <span className="flex items-center gap-2 text-indigo-900">
            <FileCheck className="h-4 w-4" /> MOH letters of approval
          </span>
          <button onClick={onClose} className="text-[#7A8A84] hover:text-[#1C1F1E]" data-testid="moh-panel-close">
            <X className="h-4 w-4" />
          </button>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {canEdit && (
          <div className="space-y-1.5 rounded-lg border border-indigo-200 bg-indigo-50/40 p-3">
            <Input value={name} onChange={(e) => setName(e.target.value)}
                   placeholder="Letter name (optional)" className="h-8 text-xs bg-white" data-testid="moh-panel-name" />
            <Input type="file" accept=".pdf" className="h-9 text-xs bg-white" disabled={uploading}
                   onChange={(e) => { upload(e.target.files[0]); e.target.value = ""; }}
                   data-testid="moh-panel-upload" />
            <p className="text-[10px] text-indigo-900/60">PDF only · not tied to a specific visitor</p>
          </div>
        )}

        {loading ? (
          <p className="text-xs text-[#7A8A84] text-center py-4">Loading…</p>
        ) : letters.length === 0 ? (
          <p className="text-xs text-[#7A8A84] italic text-center py-4">No MOH letters yet.</p>
        ) : (
          <div className="space-y-1 max-h-72 overflow-y-auto pr-1">
            {letters.map((l) => (
              <div key={l.id}
                   className={`flex items-center gap-1.5 text-xs p-2 rounded-lg border ${preview?.id === l.id ? "border-indigo-300 bg-indigo-50" : "border-transparent hover:bg-[#F4F4F1]"}`}
                   data-testid={`moh-letter-${l.id}`}>
                <button type="button" onClick={() => view(l)} className="min-w-0 flex-1 text-left" title="Preview here">
                  <p className="truncate text-[#1C1F1E] font-medium">{l.filename}</p>
                  <p className="text-[10px] text-[#7A8A84]">{(l.uploaded_at || "").slice(0, 10)}{l.uploaded_by_name ? ` · ${l.uploaded_by_name}` : ""}</p>
                </button>
                <button type="button" onClick={() => view(l)} className="shrink-0 text-indigo-700 hover:text-indigo-900" title="Preview" data-testid={`moh-view-${l.id}`}>
                  <Eye className="h-3.5 w-3.5" />
                </button>
                <button type="button" onClick={() => download(l)} className="shrink-0 text-[#40916C] hover:text-[#1B4332]" title="Download" data-testid={`moh-download-${l.id}`}>
                  <Download className="h-3.5 w-3.5" />
                </button>
                {canEdit && (
                  <button type="button" onClick={() => remove(l)} className="shrink-0 text-red-500 hover:text-red-700" title="Delete" data-testid={`moh-delete-${l.id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {preview && (
          <div className="space-y-1" data-testid="moh-preview">
            <div className="flex items-center justify-between gap-2">
              <p className="text-[11px] font-medium text-[#1C1F1E] truncate" title={preview.filename}>{preview.filename}</p>
              <div className="flex items-center gap-1.5 shrink-0">
                <button type="button" onClick={openTab} className="text-indigo-700 hover:text-indigo-900" title="Open full size in new tab" data-testid="moh-open-tab">
                  <ExternalLink className="h-3.5 w-3.5" />
                </button>
                <button type="button" onClick={() => setPreview(null)} className="text-[#7A8A84] hover:text-[#1C1F1E]" title="Close preview">
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
            <iframe src={preview.url} title={preview.filename}
                    className="w-full h-[28rem] rounded-lg border border-[#E8E8E6] bg-white" />
          </div>
        )}
      </CardContent>
    </Card>
  );
}
